// Admin: Marketplace cars table + leads inbox tabs.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Archive, Eye, ExternalLink, Inbox, Car as CarIcon, RefreshCw, ShieldCheck, ShieldAlert, ShieldX, ShieldQuestion, ChevronDown } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, notifyErr } from '../../../components/admin/billing/shared';

const STATUS_COLOR = {
  draft:          'bg-slate-100 text-slate-700',
  pending_review: 'bg-blue-100 text-blue-700',
  published:      'bg-teal-100 text-teal-800',
  rejected:       'bg-rose-100 text-rose-800',
  sold:           'bg-amber-100 text-amber-800',
  archived:       'bg-slate-200 text-slate-500',
};
const LEAD_STATUS_COLOR = {
  new:       'bg-blue-100 text-blue-800',
  contacted: 'bg-amber-100 text-amber-800',
  qualified: 'bg-purple-100 text-purple-800',
  won:       'bg-teal-100 text-teal-800',
  lost:      'bg-slate-200 text-slate-500',
};

const AdminMarketplace = () => {
  const { authHeader } = useAuth();
  const [tab, setTab] = useState('cars'); // cars | leads
  const [cars, setCars] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'cars') {
        const params = filterStatus ? `?status=${filterStatus}` : '';
        const { data } = await axios.get(`${API}/marketplace/admin/cars${params}`, { headers: authHeader() });
        setCars(data.items || []);
      } else {
        const { data } = await axios.get(`${API}/marketplace/admin/leads`, { headers: authHeader() });
        setLeads(data.items || []);
      }
    } catch (e) {
      notifyErr(e);
    } finally {
      setLoading(false);
    }
  }, [tab, filterStatus, authHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const archiveCar = async (id) => {
    if (!window.confirm('Archive this car? It will no longer be visible on the public marketplace.')) return;
    try {
      await axios.delete(`${API}/marketplace/admin/cars/${id}`, { headers: authHeader() });
      fetchData();
    } catch (e) { notifyErr(e); }
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/marketplace/admin/leads/${id}?status=${status}`, {}, { headers: authHeader() });
      fetchData();
    } catch (e) { notifyErr(e); }
  };

  // RC fraud check — inline expandable panel + re-run button.
  const [rcOpen, setRcOpen] = useState(null);
  const [rcBusy, setRcBusy] = useState(null);
  const rerunRcCheck = async (id) => {
    setRcBusy(id);
    try {
      await axios.post(`${API}/marketplace/admin/cars/${id}/rc-check`, {}, { headers: authHeader() });
      await fetchData();
      setRcOpen(id);
    } catch (e) { notifyErr(e); }
    finally { setRcBusy(null); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-marketplace">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pre-owned Marketplace</h1>
          <p className="text-sm text-slate-500">Manage listings and incoming customer leads.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg text-sm inline-flex items-center gap-1.5" data-testid="refresh-btn">
            <RefreshCw size={14} /> Refresh
          </button>
          {tab === 'cars' && (
            <Link to="/admin/marketplace/new" className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1.5" data-testid="new-car-btn">
              <Plus size={14} /> New Car
            </Link>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {[
          { key: 'cars', label: 'Cars', icon: CarIcon },
          { key: 'leads', label: 'Leads', icon: Inbox },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 inline-flex items-center gap-2 ${
              tab === t.key ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'cars' ? (
        <>
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Status:</span>
            {['', 'published', 'draft', 'sold', 'archived'].map((s) => (
              <button
                key={s || 'all'}
                onClick={() => setFilterStatus(s)}
                className={`text-xs px-3 py-1 rounded-full border ${
                  filterStatus === s
                    ? 'bg-slate-900 border-slate-900 text-white'
                    : 'bg-white border-slate-300 text-slate-700 hover:border-teal-400'
                }`}
                data-testid={`status-${s || 'all'}`}
              >
                {s || 'All'}
              </button>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Car</th>
                  <th className="text-left px-4 py-3">Year</th>
                  <th className="text-right px-4 py-3">Price</th>
                  <th className="text-right px-4 py-3">KM</th>
                  <th className="text-left px-4 py-3">Seller</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">RC Check</th>
                  <th className="text-right px-4 py-3">Views</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
                ) : cars.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No cars yet. Click &quot;New Car&quot; to list one.</td></tr>
                ) : cars.map((c) => {
                  const flags = c.rc_fraud_flags || [];
                  const redCount = flags.filter((f) => f.severity === 'red').length;
                  const amberCount = flags.filter((f) => f.severity === 'amber').length;
                  const rcStatus = c.rc_check_status;
                  let pillCls = 'bg-slate-100 text-slate-500';
                  let pillIcon = <ShieldQuestion size={12} />;
                  let pillLabel = 'Not checked';
                  if (rcStatus === 'unavailable') {
                    pillCls = 'bg-slate-100 text-slate-500'; pillIcon = <ShieldQuestion size={12} />; pillLabel = 'API not configured';
                  } else if (rcStatus === 'error') {
                    pillCls = 'bg-amber-100 text-amber-700'; pillIcon = <ShieldAlert size={12} />; pillLabel = 'Check failed';
                  } else if (rcStatus === 'ok' && redCount === 0 && amberCount === 0) {
                    pillCls = 'bg-teal-100 text-teal-800'; pillIcon = <ShieldCheck size={12} />; pillLabel = 'Verified';
                  } else if (rcStatus === 'ok' && redCount > 0) {
                    pillCls = 'bg-rose-100 text-rose-800'; pillIcon = <ShieldX size={12} />;
                    pillLabel = `${redCount} red${amberCount ? ` · ${amberCount} amber` : ''}`;
                  } else if (rcStatus === 'ok' && amberCount > 0) {
                    pillCls = 'bg-amber-100 text-amber-800'; pillIcon = <ShieldAlert size={12} />;
                    pillLabel = `${amberCount} amber`;
                  }
                  const isOpen = rcOpen === c.id;
                  return (
                  <React.Fragment key={c.id}>
                  <tr className="hover:bg-slate-50" data-testid={`car-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {c.cover_photo ? (
                          <img src={c.cover_photo} alt={`${c.make} ${c.model}`} className="w-14 h-10 object-cover rounded-md bg-slate-100" />
                        ) : (
                          <div className="w-14 h-10 rounded-md bg-slate-100 flex items-center justify-center text-slate-300 text-[10px]">no img</div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-900">{c.make} {c.model}</p>
                          <p className="text-xs text-slate-500">{c.variant || '—'} · {(c.fuel_type || '').toUpperCase()}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{c.manufacture_year}</td>
                    <td className="px-4 py-3 text-right font-semibold">{fmtINR(c.asking_price)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{(c.km_driven || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{c.seller_name}</p>
                      <p className="text-xs text-slate-500">{c.seller_phone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_COLOR[c.status] || 'bg-slate-100 text-slate-600'}`}>
                        {c.status}
                      </span>
                      {c.auto_rejected && (
                        <span
                          className="ml-1 inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-600 text-white"
                          title="Auto-rejected by the RC fraud check"
                          data-testid={`auto-rejected-${c.id}`}
                        >
                          AUTO
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setRcOpen(isOpen ? null : c.id)}
                        className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full ${pillCls} hover:opacity-80 transition`}
                        title="Click to view Surepass RC verification details"
                        data-testid={`rc-check-pill-${c.id}`}
                      >
                        {pillIcon} {pillLabel}
                        <ChevronDown size={10} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-500">{c.views || 0}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Link to={`/marketplace/${c.id}`} target="_blank" rel="noreferrer" title="View public page" className="p-1.5 hover:bg-slate-100 rounded">
                          <ExternalLink size={14} className="text-slate-500" />
                        </Link>
                        <Link to={`/admin/marketplace/${c.id}/edit`} title="Edit" className="p-1.5 hover:bg-slate-100 rounded" data-testid={`edit-${c.id}`}>
                          <Pencil size={14} className="text-teal-700" />
                        </Link>
                        {c.status !== 'archived' && (
                          <button onClick={() => archiveCar(c.id)} title="Archive" className="p-1.5 hover:bg-slate-100 rounded" data-testid={`archive-${c.id}`}>
                            <Archive size={14} className="text-rose-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50" data-testid={`rc-check-panel-${c.id}`}>
                      <td colSpan={9} className="px-6 py-4">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <div>
                            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                              Surepass · VAHAN RC Verification
                            </p>
                            {c.rc_checked_at && (
                              <p className="text-[11px] text-slate-500">
                                Last checked {new Date(c.rc_checked_at).toLocaleString('en-IN')}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => rerunRcCheck(c.id)}
                            disabled={rcBusy === c.id || !c.registration_number}
                            className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
                            title={c.registration_number ? 'Run Surepass RC verification now' : 'Listing has no registration number'}
                            data-testid={`rc-rerun-${c.id}`}
                          >
                            <RefreshCw size={12} className={rcBusy === c.id ? 'animate-spin' : ''} />
                            {rcBusy === c.id ? 'Checking…' : 'Re-run RC check'}
                          </button>
                        </div>

                        {c.rc_check_status === 'unavailable' && (
                          <p className="text-xs text-slate-500">Surepass API key is not configured on this environment. Add <code className="bg-slate-200 px-1 rounded">SUREPASS_API_KEY</code> to <code>backend/.env</code>.</p>
                        )}
                        {c.rc_check_status === 'error' && (
                          <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                            Surepass call failed: {c.rc_check_error || 'unknown error'}. Try re-running.
                          </p>
                        )}

                        {c.auto_rejected && (
                          <div className="mt-2 mb-2 text-xs bg-rose-600 text-white rounded p-2" data-testid={`auto-reject-banner-${c.id}`}>
                            <p className="font-semibold uppercase tracking-wider text-[10px] mb-1">Auto-rejected · System</p>
                            <p>Listing was moved to rejected because of the red flag(s) below. Seller has been emailed. You can still manually approve via the row action.</p>
                          </div>
                        )}

                        {flags.length > 0 && (
                          <ul className="mt-2 space-y-1.5" data-testid={`rc-flags-${c.id}`}>
                            {flags.map((f) => (
                              <li key={f.code} className={`flex items-start gap-2 text-xs rounded p-2 ${
                                f.severity === 'red' ? 'bg-rose-50 text-rose-800 border border-rose-200'
                                                     : 'bg-amber-50 text-amber-900 border border-amber-200'
                              }`}>
                                {f.severity === 'red'
                                  ? <ShieldX size={13} className="mt-0.5 flex-shrink-0" />
                                  : <ShieldAlert size={13} className="mt-0.5 flex-shrink-0" />}
                                <span>{f.message}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {c.rc_verification && (
                          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1 text-xs" data-testid={`rc-details-${c.id}`}>
                            {[
                              ['Owner', c.rc_verification.owner_name],
                              ['Maker', [c.rc_verification.maker_description, c.rc_verification.maker_model].filter(Boolean).join(' ')],
                              ['Reg. Date', c.rc_verification.registration_date],
                              ['Fitness Upto', c.rc_verification.fitness_upto],
                              ['Insurance Upto', c.rc_verification.insurance_upto],
                              ['PUC Upto', c.rc_verification.puc_upto],
                              ['RTO', c.rc_verification.rto],
                              ['RC Status', c.rc_verification.rc_status],
                              ['Financer', c.rc_verification.financer],
                            ].filter(([, v]) => v).map(([k, v]) => (
                              <div key={k} className="flex justify-between border-b border-slate-200 py-1">
                                <span className="text-slate-500">{k}</span>
                                <span className="text-slate-900 text-right font-medium">{v}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );})}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Kind</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Car</th>
                <th className="text-left px-4 py-3">Message</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No leads yet.</td></tr>
              ) : leads.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    {l.created_at ? new Date(l.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                      {l.kind}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{l.name}</p>
                    <p className="text-xs text-slate-500">{l.phone}{l.email ? ` · ${l.email}` : ''}</p>
                  </td>
                  <td className="px-4 py-3">
                    {l.car_snapshot ? (
                      <Link to={`/admin/marketplace/${l.car_id}/edit`} className="text-teal-700 hover:underline text-xs inline-flex items-center gap-1">
                        <Eye size={12} /> {l.car_snapshot.manufacture_year} {l.car_snapshot.make} {l.car_snapshot.model}
                      </Link>
                    ) : (
                      <span className="text-xs text-slate-400">General enquiry</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700 max-w-xs">
                    <p className="line-clamp-2 text-xs">{l.message || '—'}</p>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={l.status}
                      onChange={(e) => updateLeadStatus(l.id, e.target.value)}
                      className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${LEAD_STATUS_COLOR[l.status] || 'bg-slate-100'}`}
                      data-testid={`lead-status-${l.id}`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="won">Won</option>
                      <option value="lost">Lost</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminMarketplace;
